/**
 * „Möglicherweise Bestandskunde" — Dedup-Plan Phase 3 (30.08.2026).
 *
 * Antwort von `GET /api/inquiries/:id/dubletten-hinweis`: mögliche Bestandskunden, wenn das
 * E-Mail-Matching nicht greift, aber die Telefon-Normalform der Anfrage lebende Kunden trifft.
 * NUR ein Vorschlag (Antoine, verbindlich: nie automatisch zuordnen) — übernommen wird
 * ausschliesslich durch die explizite Auswahl hier, die dann als `customerId` an
 * `komponieren`/`manuell-uebernehmen` geht (s. `hooks/useAngebotUebernahme.ts`).
 *
 * Bis zum Arbeitspaket F1 (`docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md` Teil B2) lag dieses
 * Markup inline in `pages/anfrage-detail.tsx`. Seit die Übernahme-Aktionen auch auf der
 * Vorgangsseite stehen, brauchen BEIDE Seiten den Hinweis an derselben Stelle — sonst wählte man
 * dort einen Bestandskunden, der bei der Übernahme gar nicht mitginge.
 *
 * ## Warum die Karte kontrolliert ist
 *
 * Die Auswahl (`gewaehlteKundeId`) gehört der aufrufenden Seite, nicht dieser Karte: sie geht als
 * `uebernahmeKundeId` in `useAngebotUebernahme` und damit in den Request-Body. Eine Karte, die
 * ihre Wahl für sich behielte, wäre eine Anzeige ohne Wirkung.
 *
 * ## Design-System
 *
 * Die beiden Knöpfe sind `Button` aus `ui/components.tsx` statt der rohen
 * `btn-primary`/`btn-secondary`-Klassen der Quelle (CLAUDE.md, CRITICAL-Pflicht zu den
 * vereinheitlichten Komponenten) — gerenderte Klasse und damit die Optik sind identisch.
 * Der Karten-Rahmen bleibt bewusst der eigene, primär eingefärbte Kasten und wird NICHT auf
 * `Card` umgestellt: `.card` ist die neutrale weisse Karte, die Einfärbung ist hier die ganze
 * Aussage („schau hier hin"), und `pages/anfrage-detail.tsx` darf sich durch diese Extraktion
 * für den Betreiber nicht verändern.
 */
import { Link } from "react-router-dom";
import { useApi } from "../../hooks/useApi";
import { Button, Icon } from "../components";

export interface DublettenTreffer {
  id: string;
  name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  events: number;
}

export interface DublettenHinweis {
  grund: "telefon";
  telefonNormalisiert: string;
  treffer: DublettenTreffer[];
}

export interface DublettenHinweisKarteProps {
  /** Anfrage, zu der der Hinweis gehört. `null` → die Karte lädt nichts und zeigt nichts. */
  inquiryId: string | null;
  /** Aktuell gewählter Bestandskunde (`null` = neuer Kunde, der Normalfall). */
  gewaehlteKundeId: string | null;
  /** Auswahl umschalten — `null` beim Abwählen. */
  onKundeGewaehlt: (customerId: string | null) => void;
}

export function DublettenHinweisKarte({ inquiryId, gewaehlteKundeId, onKundeGewaehlt }: DublettenHinweisKarteProps) {
  const hinweisQ = useApi<{ hinweis: DublettenHinweis | null }>(
    inquiryId ? `/api/inquiries/${inquiryId}/dubletten-hinweis` : null,
  );
  const dublettenHinweis = hinweisQ.data?.hinweis ?? null;
  if (!dublettenHinweis || dublettenHinweis.treffer.length === 0) return null;

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3.5 dark:border-primary/40 dark:bg-primary/10" data-testid="dubletten-hinweis">
      <p className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-primary">
        <Icon name="person_search" className="text-[14px]" /> Möglicherweise Bestandskunde
      </p>
      <p className="mb-2.5 font-body-sm text-[12px] leading-relaxed text-on-surface-variant dark:text-secondary-fixed-dim">
        Die Telefonnummer dieser Anfrage ({dublettenHinweis.telefonNormalisiert}) gehört bereits{" "}
        {dublettenHinweis.treffer.length === 1 ? "einem Bestandskunden" : "Bestandskunden"}. Ohne Auswahl legt
        „KI-Angebot erzeugen“ und „Manuell erstellen“ wie bisher einen neuen Kunden an — zugeordnet wird nur, was
        du hier ausdrücklich übernimmst.
      </p>
      <ul className="space-y-1.5">
        {dublettenHinweis.treffer.map((t) => {
          const gewaehlt = gewaehlteKundeId === t.id;
          return (
            <li
              key={t.id}
              className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border px-3 py-2 ${
                gewaehlt
                  ? "border-primary bg-primary/10 dark:bg-primary/15"
                  : "border-outline-variant/40 bg-surface-container-lowest dark:border-outline/30 dark:bg-night-card"
              }`}
            >
              <div className="min-w-0 flex-1">
                <Link
                  to={`/kunden/${t.id}`}
                  className="block max-w-full truncate font-body-sm text-[12.5px] font-semibold text-on-background hover:text-primary hover:underline dark:text-inverse-on-surface"
                >
                  {t.name}
                  {t.company ? ` · ${t.company}` : ""}
                </Link>
                <p className="truncate font-body-sm text-[11px] text-on-surface-variant">
                  {[t.email, t.phone, `${t.events} Vorgänge`].filter(Boolean).join(" · ")}
                </p>
              </div>
              <Button
                type="button"
                variant={gewaehlt ? "primary" : "secondary"}
                className="!py-1.5"
                onClick={() => onKundeGewaehlt(gewaehlt ? null : t.id)}
              >
                {gewaehlt ? (
                  <>
                    <Icon name="check" className="text-[15px]" /> Wird übernommen
                  </>
                ) : (
                  "Diesen Kunden verwenden"
                )}
              </Button>
            </li>
          );
        })}
      </ul>
      {gewaehlteKundeId && (
        <p className="mt-2 font-body-sm text-[11px] text-on-surface-variant/80">
          Beim Erzeugen des Angebots wird der ausgewählte Bestandskunde verwendet — es entsteht kein neuer Kundendatensatz.
        </p>
      )}
    </div>
  );
}
